import { GetStaticProps, InferGetStaticPropsType } from 'next'
import { MDXRemote, MDXRemoteSerializeResult } from 'next-mdx-remote'

import { GradientBlock } from '@/components/blocks/gradient-block'
import { BlueImage } from '@/components/blue-image'
import { BookData } from '@/components/book-card'
import { Footer, FooterProps } from '@/components/footer/footer'
import { Header } from '@/components/header/header'
import { TransLink } from '@/components/trans-link'
import { getAllSlugs } from '@/helper/get-all-slugs'
import { getBook } from '@/helper/get-books'
import { getImageSize } from '@/helper/get-image-size'
import { getMenuData } from '@/helper/get-menu-data'

export interface SingleBookProps {
  data: BookData
  source: MDXRemoteSerializeResult<Record<string, unknown>>
  menuData: FooterProps['data']
}

export default function BookSingleView(
  props: InferGetStaticPropsType<typeof getStaticProps>
) {
  if (!props || props == {}) return null
  const { data, menuData } = props as SingleBookProps
  const { title, description, imageSrc, author, width, height } = data

  return (
    <>
      <Header small />
      <h1>{title}</h1>
      <main className="serif">
        <div className="content">
          <MDXRemote
            {...description}
            components={{ GradientBlock: GradientBlock }}
          />
          <ul className="links">
            <li>
              Mehr über{' '}
              <TransLink href={`/authors/${author.slug}`}>
                {author.fullName}
              </TransLink>
            </li>
            <li>
              Zur <TransLink href="/#📖">Bücherübersicht</TransLink>
            </li>
          </ul>
        </div>
        {imageSrc ? (
          <BlueImage
            imageSrc={imageSrc}
            alt={`Buchcover ${title}`}
            blue={false}
            width={width}
            height={height}
            maxWidth="30"
          />
        ) : (
          <div className="spacer">&nbsp;</div>
        )}
      </main>

      <style jsx>{`
        main {
          margin-top: 8rem;
          .content {
            margin-top: 8rem;
          }
          @media (min-width: 780px) {
            display: flex;
            justify-content: space-between;
            .content {
              max-width: 47%;
            }
          }
        }
        .links {
          margin-top: 10rem;
        }
      `}</style>
      <Footer data={menuData} />
    </>
  )
}

export const getStaticProps: GetStaticProps<SingleBookProps | {}> = async ({
  params,
}) => {
  const slug = params?.slug?.toString()
  if (!slug) return { props: {} }

  const data = await getBook(slug)
  // const mdxSource = await serialize(data.description)

  const menuData = getMenuData()

  const sizes = getImageSize(data)

  return {
    props: { data: { ...data, ...sizes }, menuData },
  }
}

export async function getStaticPaths() {
  const slugs = getAllSlugs('books')

  return {
    paths: slugs.map((slug) => {
      return {
        params: {
          slug,
        },
      }
    }),
    fallback: false,
  }
}
